import { defineComponent } from 'vue';
import { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort';
import { useToaster } from '@/composables/useToaster';

import {
  Route,
  Query,
  SortData,
  CourseBasic,
  CoursesListResponse,
  CourseListProvider,
} from '@/types/course.type';
import { Lang } from '@/types/general.type';

export default defineComponent({
  name: 'CourseListMixin',
  setup() {
    return {
      ...useToaster(),
    };
  },
  inject: ['Provider'],
  emits: ['update:filters'],
  props: {
    filters: {
      type: Object,
      default: () => ({ courseTypes: [] }),
    },
    showAction: Boolean,
    showAddBtn: Boolean,
    appId: String,
    name: String,
  },
  data() {
    return {
      userRights: [] as string[] | undefined,
      headerClass: 'grey darken-4 secondary--text pa-3 qb-table__header',
      loading: false,
      queries: {} as Query,

      loadingFilters: false,
      courseList: [] as CourseBasic[],
      totalItems: 0,
      limit: 10,
      page: 1,

      // Server-side table variables
      tablePage: 1,
      tableLimit: 10,
      sortKeys: [] as SortItem[],

      filtersInputs: {
        courseName: '',
        stdPlan: '',
        IHId: '',
        courseType: '',
        activeStatus: '',
        validFilters: false,
      } as Record<string, any>,

      currentSelectedScope: {
        selectedScopeId: '',
      },

      limitOption: [5, 10, 20, 50],

      // Prevent concurrent fetch
      isFetching: false,
    };
  },
  computed: {
    headerOptions() {
      return { sortByText: this.$t('labels.sortBy') };
    },
    filtersOptions: {
      get() {
        return this.filters;
      },
      set(newValue) {
        this.$emit('update:filters', newValue);
      },
    },
  },
  created() {
    this.setQueries();
  },
  mounted() {
    this.$store.commit('resetCourse');
    this.$store.commit('resetAppFilters');
    this.fetchFiltersData();
    this.getCoursesList();
  },
  methods: {
    fetchFiltersData() {
      Object.keys(this.filtersInputs).forEach((key: string) => {
        this.filtersInputs[key] = this.queries[key] ? this.queries[key] : '';
      });
    },
    resetSearch() {
      Object.keys(this.filtersInputs).forEach((key: string) => {
        if (key !== 'validFilters') this.filtersInputs[key] = '';
      });
      this.resetScope();
      this.search();
    },
    search() {
      const queryData = { ...this.filtersInputs };
      delete queryData.validFilters;
      this.queries = { ...queryData } as unknown as Query;

      this.tablePage = 1;
      this.getCoursesList();
    },
    async getCoursesList() {
      if (this.isFetching) return;

      try {
        this.loading = true;
        this.isFetching = true;

        const totalPages = Math.ceil(this.totalItems / this.tableLimit);
        if (this.tablePage > totalPages && totalPages > 0) {
          this.tablePage = totalPages;
        }

        const reqQuery = {
          ...this.queries,
          limit: `${this.tableLimit}`,
          page: `${this.tablePage}`,
          clientLang: this.$store.state.lang.lang,
        };

        // Safe sorting data
        const sortData: SortData = {};
        if (this.sortKeys && this.sortKeys.length > 0 && this.sortKeys[0]) {
          sortData.orderBy = this.sortKeys[0].key;
          sortData.desc = this.sortKeys[0].order === 'desc' ? 'true' : undefined;
        }

        this.currentSelectedScope = { ...this.$store.state.scope };
        const { selectedScopeId } = this.currentSelectedScope;

        const {
          courses,
          currentPage,
          totalItems,
          courseTypes,
          rights,
        }: CoursesListResponse = await (this.Provider as CourseListProvider).getAppCourseList(
          this.appId ?? '',
          {
            ...reqQuery,
            ...sortData,
            scope: selectedScopeId ?? '',
          },
        );

        this.loading = false;
        this.isFetching = false;
        this.loadingFilters = false;

        this.userRights = rights;
        this.courseList = courses;
        this.totalItems = Number(totalItems);

        // Sync table props after fetch
        this.tablePage = currentPage;
        this.tableLimit = Number(reqQuery.limit);

        // Update filters data
        this.filtersOptions.courseTypes = courseTypes;
        this.$store.commit('CourseFilters', { ...reqQuery, ...sortData }); 
        this.$router.replace({ query: { ...reqQuery, ...sortData } });
      } catch (err) {
        this.errorToaster(err as Lang, { duration: 5000 });
        this.loading = false;
        this.isFetching = false;
        this.loadingFilters = false;
        this.courseList = [];
        this.totalItems = 0;
      }
    },
    setQueries() {
      const route: Route = this.$route as Route;
      Object.assign(this.queries, route.query);

      const page = Number(this.queries.page) || 1;
      const limit = Number(this.queries.limit) || 10;

      this.tablePage = page;
      this.tableLimit = limit;

      this.sortKeys = [];
      if (this.queries.orderBy !== undefined) {
        this.sortKeys.push({
          key: this.queries.orderBy,
          order: this.queries.desc === 'true' ? 'desc' : 'asc',
        });
      }
    },
    getItemIndex(item: any) {
      return this.courseList.indexOf(item);
    },
    resetScope() {
      this.$store.commit('resetScope');
    },

    // NEW: Table options update handler for v-data-table-server
    onTableOptionsUpdate(options: { page: number; itemsPerPage: number; sortBy: any[] }) {
      this.tablePage = options.page;
      this.tableLimit = options.itemsPerPage;
      this.sortKeys = options.sortBy;

      this.getCoursesList();
    },
  },
});
